import React, { createElement, useMemo } from 'react';

import { Skeleton, Typography } from 'lighthouse';

import { rialToToman } from '@/utilities/Financial';
import {
    isShippingFulfillment,
    isZeroLaborFeeFulfillment,
    resolveFulfillmentServiceType,
    resolveOfferFulfillmentProfiles
} from '@/utilities/fulfillment';
import { withSeparator } from '@/utilities/withSeparator';

import { SERVICE_TYPES } from '@/constants/FullfilmentTypes';

import { DeliveryMethodOption, DeliveryMethodsProps } from './models';

const SERVICE_TYPE_ENTRIES = Object.entries(SERVICE_TYPES)
    .filter(([, config]) => !config.disabled)
    .sort(([, first], [, second]) => first.priority - second.priority);
const SKELETON_CARDS = [0, 1, 2];

const getDeliveryFee = (profile: DeliveryMethodOption['fulfillmentProfile']) => {
    if (isShippingFulfillment(profile.fulfillmentType)) {
        return profile.isFree ? 0 : profile.minBaseCost || 0;
    }

    if (isZeroLaborFeeFulfillment(profile.fulfillmentType)) {
        return 0;
    }

    return profile.laborFee || 0;
};

const formatDeliveryFeeLabel = (fee: number) => {
    if (!fee) {
        return <span className='!text-xs md:!text-sm'>رایگان</span>;
    }

    return (
        <>
            {withSeparator(rialToToman(fee))}
            <span className='mr-1 text-[11px] font-semibold text-blackHighEmphasis md:text-xs'>تومان</span>
        </>
    );
};

export const DeliveryMethods = ({
    seller,
    value,
    onChange,
    compact = false,
    disabled = false,
    unavailable = false,
    isLoading = false
}: DeliveryMethodsProps) => {
    const options = useMemo<DeliveryMethodOption[]>(() => {
        const profiles = resolveOfferFulfillmentProfiles(seller).sort(
            (first, second) =>
                resolveFulfillmentServiceType(first.fulfillmentType).priority - resolveFulfillmentServiceType(second.fulfillmentType).priority
        );
        if (!profiles.length) return [];

        return profiles.map((profile) => {
            const serviceType = resolveFulfillmentServiceType(profile.fulfillmentType);
            const deliveryFee = getDeliveryFee(profile);

            return {
                id: profile.fulfillmentProfileId,
                label: serviceType.label,
                description:
                    profile.fulfillmentType === 'SHIPPING'
                        ? profile.minOffsetDays > 0
                            ? `ارسال از ${profile.minOffsetDays} روز کاری دیگر`
                            : `ارسال عادی فروشنده`
                        : serviceType.description,
                feeLabel: serviceType.feeLabel,
                deliveryFee,
                isFree: deliveryFee === 0,
                fulfillmentProfile: profile
            };
        });
    }, [seller]);

    const showFallbackServiceTypes = !options.length && unavailable;

    if (!isLoading && !options.length && !showFallbackServiceTypes) return null;

    return (
        <div className='mt-4'>
            {!compact && (
                <Typography variant='subtitle2' fontWeight={700} color='blackHighEmphasis' component='h2' className='mb-2 !text-sm md:!text-base'>
                    روش‌ تحویل
                </Typography>
            )}

            {isLoading ? (
                <ul className='flex list-none flex-col gap-2 p-0' aria-busy aria-label='در حال بارگذاری روش‌های تحویل'>
                    {SKELETON_CARDS.map((index) => (
                        <li key={index}>
                            <div
                                className='flex w-full items-center justify-between gap-3 rounded-lg border border-solid border-gray-100 bg-white p-3'>
                                <span className='flex min-w-0 flex-1 items-center gap-2'>
                                    <Skeleton variant='circular' width={28} height={28} className='shrink-0' />
                                    <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
                                        <Skeleton variant='rectangular' width={96} height={12} className='rounded' />
                                        <Skeleton variant='rectangular' width={140} height={10} className='rounded' />
                                    </span>
                                </span>
                                <Skeleton variant='rectangular' width={56} height={14} className='shrink-0 rounded' />
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <ul className='flex list-none flex-col gap-2 p-0' role='radiogroup' aria-label='روش‌های تحویل' aria-disabled={disabled}>
                    {showFallbackServiceTypes
                        ? SERVICE_TYPE_ENTRIES.map(([key, serviceType]) => (
                              <li key={key}>
                                  <button
                                      type='button'
                                      role='radio'
                                      aria-checked={false}
                                      disabled
                                      className='flex w-full cursor-not-allowed flex-col gap-2 rounded-lg border border-solid border-gray-100
                                        bg-gray-50 p-3 text-right opacity-80 [touch-action:manipulation]'>
                                      <div className='flex w-full items-start justify-between gap-3'>
                                          <span className='flex min-w-0 flex-1 items-start gap-2'>
                                              <span
                                                  className='mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10
                                                    text-primary'>
                                                  {createElement(serviceType.icon, {
                                                      color: 'currentcolor',
                                                      className: 'size-5'
                                                  })}
                                              </span>
                                              <span className='flex min-w-0 flex-1 flex-col gap-1'>
                                                  <Typography
                                                      variant='body2'
                                                      fontWeight={700}
                                                      color='blackHighEmphasis'
                                                      className='!text-xs md:!text-sm'>
                                                      {serviceType.label}
                                                  </Typography>
                                                  <Typography variant='caption' color='blackMediumEmphasis' className='!text-[11px] md:!text-xs'>
                                                      {serviceType.description}
                                                  </Typography>
                                              </span>
                                          </span>
                                      </div>
                                  </button>
                              </li>
                          ))
                        : options.map((option) => {
                              const itemIcon = resolveFulfillmentServiceType(option.fulfillmentProfile.fulfillmentType).icon;
                              const isDisabled = disabled || option.fulfillmentProfile.isEligible !== true;
                              const isSelected = !isDisabled && value?.fulfillmentProfileId === option.fulfillmentProfile.fulfillmentProfileId;
                              const optionCardClassName = isDisabled
                                  ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-80'
                                  : isSelected
                                    ? 'border-primary bg-primary/10 shadow-sm'
                                    : 'border-gray-100 bg-white hover:border-primary/40 hover:bg-primary/[0.02]';

                              return (
                                  <li key={`${option.fulfillmentProfile.fulfillmentType}-${option.id}`}>
                                      <button
                                          type='button'
                                          role='radio'
                                          aria-checked={isSelected}
                                          disabled={isDisabled}
                                          onClick={() => onChange(option.fulfillmentProfile)}
                                          className={`flex w-full flex-col gap-2 rounded-lg border border-solid p-3 text-right transition-all
                                            duration-200 [touch-action:manipulation] ${optionCardClassName}`}>
                                          <div className='flex w-full items-start justify-between gap-3'>
                                              <span className='flex min-w-0 flex-1 items-start gap-2'>
                                                  <span
                                                      className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full
                                                        ${!isDisabled && isSelected ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                                                      {createElement(itemIcon, {
                                                          color: 'currentcolor',
                                                          className: 'size-5'
                                                      })}
                                                  </span>
                                                  <span className='flex min-w-0 flex-1 flex-col gap-1'>
                                                      <Typography
                                                          variant='body2'
                                                          fontWeight={700}
                                                          color={!isDisabled && isSelected ? 'primary' : 'blackHighEmphasis'}
                                                          className='!text-xs md:!text-sm'>
                                                          {option.label}
                                                      </Typography>
                                                      <Typography variant='caption' color='blackMediumEmphasis' className='!text-[11px] md:!text-xs'>
                                                          {option.fulfillmentProfile.isEligible === true
                                                              ? option.description
                                                              : 'برای موقعیت شما قابل ارائه نیست'}
                                                      </Typography>
                                                  </span>
                                              </span>

                                              {!unavailable && !['SHIPPING', 'PICKUP_CENTER'].includes(option.fulfillmentProfile.fulfillmentType) && (
                                                  <span className='flex shrink-0 flex-col items-end gap-0.5'>
                                                      <Typography
                                                          variant='overline'
                                                          color='blackMediumEmphasis'
                                                          className='!text-[10px] md:!text-[11px]'>
                                                          {option.feeLabel}
                                                      </Typography>
                                                      <Typography
                                                          variant='subtitle2'
                                                          color={isDisabled ? 'blackMediumEmphasis' : 'primary'}
                                                          fontWeight={700}
                                                          className='!text-sm md:!text-base'>
                                                          {formatDeliveryFeeLabel(option.deliveryFee)}
                                                      </Typography>
                                                  </span>
                                              )}
                                          </div>
                                      </button>
                                  </li>
                              );
                          })}
                </ul>
            )}
        </div>
    );
};
